import React from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Users, TimerReset, Download, Inbox,
  Building2, LogOut,
} from "lucide-react";
import { getAdminToken, setAdminToken, getAdminEmail } from "@/lib/adminApi";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/delegates", label: "Confirmed Delegates", icon: Users },
  { to: "/admin/abandoned", label: "Abandoned Checkouts", icon: TimerReset, hot: true },
  { to: "/admin/downloads", label: "Download Leads", icon: Download, hot: true },
  { to: "/admin/leads", label: "All Leads Inbox", icon: Inbox },
  { to: "/admin/exhibitors", label: "Exhibitors", icon: Building2 },
];

export default function AdminLayout() {
  const nav = useNavigate();
  const token = getAdminToken();
  if (!token) return <Navigate to="/admin/login" replace />;

  const signOut = () => {
    setAdminToken(null);
    localStorage.removeItem("bef_admin_email");
    nav("/admin/login", { replace: true });
  };
  const email = getAdminEmail();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="hidden md:flex md:w-64 flex-col border-r border-slate-200 bg-white">
          <div className="px-5 py-5 border-b border-slate-100">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-lime-600">Event OS</div>
            <div className="mt-0.5 font-display text-lg font-bold text-slate-900 leading-tight">Bharat Ethanol<br/>Forum 2026</div>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                    isActive
                      ? "bg-slate-900 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                <n.icon className="h-4 w-4" />
                <span className="flex-1">{n.label}</span>
                {n.hot && (
                  <span className="rounded-full bg-lime-400 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-900">
                    Hot
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-slate-100">
            {email && (
              <div className="mb-2 px-3 py-2 rounded-lg bg-slate-50">
                <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Signed in as</div>
                <div className="text-xs font-semibold text-slate-800 truncate">{email}</div>
              </div>
            )}
            <button
              data-testid="admin-signout"
              onClick={signOut}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0">
          {/* Mobile top bar */}
          <div className="md:hidden sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/95 backdrop-blur px-4 py-3">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-lime-600">Event OS</div>
              <div className="text-sm font-bold text-slate-900">BEF 2026 Admin</div>
            </div>
            <button onClick={signOut} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">
              <LogOut className="inline h-3.5 w-3.5" />
            </button>
          </div>
          {/* mobile nav pills */}
          <div className="md:hidden overflow-x-auto border-b border-slate-100 bg-white px-3 py-2">
            <div className="flex gap-2 whitespace-nowrap">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    `rounded-full px-3 py-1.5 text-xs font-semibold ${
                      isActive ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>

          <div className="p-4 md:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
